<script setup lang="ts">
import { MapPin, Instagram } from 'lucide-vue-next'

const currentYear = new Date().getFullYear()

interface SiteMapItem {
  name: string
  path: string
  external?: boolean // Vue Routerを使わず通常の<a>タグで遷移（内部・外部リンク共通）
  blank?: boolean    // 新しいタブで開く
}

const siteMapItems: SiteMapItem[] = [
  { name: 'TOP', path: '/' },
  { name: '料金', path: '/pricing' },
  { name: '使い方', path: '/how-to-use' },
  { name: '設備', path: '/equipment' },
  { name: 'よくある質問', path: '/faq' },
  { name: 'アクセス', path: '/access' },
  { name: '予約状況', path: '/availability', external: true },
  { name: 'お問い合わせ', path: '/contact' },
  { name: 'プライバシーポリシー', path: '/privacy-policy' },
  { name: '特定商取引法に基づく表示', path: '/legal/commerce' },
  { name: '会社概要', path: 'https://www.play-gate.com/company-profile', external: true, blank: true },
]
</script>

<template>
  <footer class="relative bg-[#5a3a26] text-white pt-20 pb-10 border-t-8 border-lime-400">
    <!-- Decorative Top Border -->
    <div class="absolute top-0 left-0 w-full h-4 bg-[repeating-linear-gradient(45deg,#3f2618,#3f2618_10px,#84cc16_10px,#84cc16_20px)]" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div class="col-span-1 md:col-span-2">
          <router-link
            to="/"
            class="inline-block mb-6 group"
          >
            <img
              :src="'/images/hideout/logo.png'"
              alt="HIDE OUT NAGAI"
              width="186"
              height="64"
              loading="lazy"
              class="h-16 w-auto object-contain filter brightness-0 invert drop-shadow-[0_0_5px_rgba(132,204,22,0.8)]"
            >
          </router-link>
          <p class="text-[#d4b896] text-sm mb-8 max-w-sm font-bold leading-loose border-l-4 border-lime-400 pl-4">
            24時間無人アミューズメント施設。<br>
            あなただけの秘密基地で、自由な時間を。<br>
            <span class="text-lime-400">PRESS START TO PLAY.</span>
          </p>
          <div class="flex space-x-4">
            <!-- TODO: 実際のInstagramアカウントURLに置き換えてください -->
            <a
              href="https://www.instagram.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              class="w-12 h-12 flex items-center justify-center bg-[#3f2618] border-2 border-[#8b5a3b] hover:bg-lime-400 hover:text-[#5a3a26] hover:border-lime-400 transition-all shadow-[4px_4px_0px_0px_#3f2618]"
            >
              <Instagram :size="24" />
            </a>
            <!-- TODO: 実際のX(旧Twitter)アカウントURLに置き換えてください -->
            <a
              href="https://x.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              class="w-12 h-12 flex items-center justify-center bg-[#3f2618] border-2 border-[#8b5a3b] hover:bg-cyan-400 hover:text-[#5a3a26] hover:border-cyan-400 transition-all shadow-[4px_4px_0px_0px_#3f2618]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
        </div>

        <div>
          <h3 class="text-lime-400 font-black mb-6 uppercase tracking-widest border-b-2 border-[#8b5a3b] pb-2 inline-block">
            サイトマップ
          </h3>
          <ul class="space-y-3 text-sm font-bold">
            <li
              v-for="item in siteMapItems"
              :key="item.path"
            >
              <a
                v-if="item.external"
                :href="item.path"
                :target="item.blank ? '_blank' : undefined"
                :rel="item.blank ? 'noopener noreferrer' : undefined"
                class="text-[#d4b896] hover:text-white hover:translate-x-2 transition-transform inline-flex items-center gap-2"
              >
                <span class="w-1.5 h-1.5 bg-lime-400" />
                {{ item.name }}
              </a>
              <router-link
                v-else
                :to="item.path"
                class="text-[#d4b896] hover:text-white hover:translate-x-2 transition-transform inline-flex items-center gap-2"
              >
                <span class="w-1.5 h-1.5 bg-lime-400" />
                {{ item.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <div>
          <h3 class="text-lime-400 font-black mb-6 uppercase tracking-widest border-b-2 border-[#8b5a3b] pb-2 inline-block">
            アクセス
          </h3>
          <div class="flex items-start gap-3 text-sm text-[#d4b896] mb-4 font-bold">
            <MapPin
              :size="20"
              class="mt-1 shrink-0 text-lime-400"
            />
            <span>
              〒993-0002<br>
              山形県長井市屋城町1-40 2F
            </span>
          </div>
          <div class="bg-[#3f2618] p-4 border-2 border-[#8b5a3b] rounded-sm">
            <p class="text-xs text-lime-400 font-mono mb-1">
              // NOTICE
            </p>
            <p class="text-xs text-[#d4b896] font-bold">
              無人店舗のため、お問い合わせはLINEにてお願いいたします。
            </p>
          </div>
        </div>
      </div>

      <div class="border-t-2 border-[#8b5a3b] pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-[#d4b896] font-bold uppercase tracking-wider">
        <p>&copy; {{ currentYear }} HIDE OUT NAGAI. All rights reserved.</p>
        <p class="flex items-center gap-2">
          POWERED BY <span class="text-lime-400">PlayGate</span>
        </p>
      </div>
    </div>
  </footer>
</template>
